import { UseAddItemsMutationOptions } from "@ac-expo/api";
import { EventDetails } from "@ac-expo/api-types";
import {
  getBuyableItemGroupKey,
  getGroupPurchaseMinimumMessage,
  isGroupPurchaseMinimumUnmet,
} from "@ac-expo/utils";
import { BasketType } from "@prisma/client";
import { useMemo, useState } from "react";
import { useHydratedLocalCart } from "./useHydratedLocalCart";
import { BuyableItemWithPricing } from "./useLocalCart";

export type EntryType = "badge" | "ticket" | "activity";

export type BuyableEntryType<T extends EntryType> = (T extends "badge"
  ? EventDetails["badgeTypes"]
  : EventDetails["ticketTypes"])[number];

export type BuyableEntryTypeWithKey<T extends EntryType> = {
  entryType: BuyableEntryType<T>;
  itemKey: string;
};

export const useTicketing = ({
  onAddToCartMutationSuccess,
  onAddToCartMutationError,
  ticketTypes = [],
  badgeTypes = [],
  activityTicketTypes = [],
  availableEntries,
  basketType,
  isAuthenticated,
  onItemValidationError,
}: {
  onAddToCartMutationSuccess?: UseAddItemsMutationOptions["onSuccess"];
  onAddToCartMutationError?: UseAddItemsMutationOptions["onError"];
  ticketTypes?: EventDetails["ticketTypes"];
  badgeTypes?: EventDetails["badgeTypes"];
  activityTicketTypes?: EventDetails["ticketTypes"];
  availableEntries: BuyableItemWithPricing[];
  basketType: BasketType;
  isAuthenticated: boolean;
  onItemValidationError?: (error: string) => void;
}) => {
  const [error, setError] = useState("");

  const { localCart, mutateCart, ...rest } = useHydratedLocalCart({
    basketType,
    availableBuyableItems: availableEntries,
    isAuthenticated,
    onAddToCartMutationSuccess,
    onAddToCartMutationError,
  });

  const { cartItems, resetCart } = localCart;

  const filteredCartItems = cartItems.filter((item) => item.quantity > 0);

  const areItemsBuyable = () => {
    const cartTickets = filteredCartItems.filter(
      (item) => item.buyableItem.buyableType === "ticket",
    );
    for (const ticket of cartTickets) {
      const ticketTypeId = ticket.buyableItem.ticketType?.id;
      if (!ticketTypeId) {
        throw new Error("Ticket type ID is required");
      }
      const ticketType = [...ticketTypes, ...activityTicketTypes].find(
        (t) => t.id === ticketTypeId,
      );
      if (!ticketType) {
        throw new Error("Ticket type not found");
      }

      const validationError = getGroupPurchaseMinimumMessage(ticketType, ticket.quantity);

      if (validationError) {
        onItemValidationError?.(validationError);
        setError(validationError);
        return false;
      }
    }
    return true;
  };

  // The « Group purchase » minimum is already worded once, under the line that is short of it:
  // a screen gates its button on this flag rather than showing the wording a second time.
  // Badges carry the same minimum as tickets, and the server refuses both.
  const hasGroupPurchaseMinimumError = filteredCartItems.some((item) => {
    const { ticketType, badgeType } = item.buyableItem;

    const entryType = ticketType
      ? [...ticketTypes, ...activityTicketTypes].find((type) => type.id === ticketType.id)
      : badgeTypes.find((type) => type.id === badgeType?.id);

    return !!entryType && isGroupPurchaseMinimumUnmet(entryType, item.quantity);
  });

  const handleAddToCart = () => {
    if (areItemsBuyable()) {
      setError("");
      mutateCart({
        onSuccess: () => {
          resetCart();
        },
      });
    }
  };

  const buyableTicketTypes: BuyableEntryTypeWithKey<"ticket">[] = useMemo(() => {
    return ticketTypes.map((ticketType) => ({
      entryType: ticketType,
      itemKey: getBuyableItemGroupKey({
        buyableType: "ticket",
        ticketType: { id: ticketType.id },
      }),
    }));
  }, [ticketTypes]);

  const buyableActivityTicketTypes: BuyableEntryTypeWithKey<"activity">[] = useMemo(() => {
    return activityTicketTypes.map((ticketType) => ({
      entryType: ticketType,
      itemKey: getBuyableItemGroupKey({
        buyableType: "ticket",
        ticketType: { id: ticketType.id },
      }),
    }));
  }, [activityTicketTypes]);

  const buyableBadgeTypes: BuyableEntryTypeWithKey<"badge">[] = useMemo(() => {
    return badgeTypes.map((badgeType) => ({
      entryType: badgeType,
      itemKey: getBuyableItemGroupKey({
        buyableType: "badge",
        badgeType: { id: badgeType.id },
      }),
    }));
  }, [badgeTypes]);

  return {
    error,
    hasGroupPurchaseMinimumError,
    localCart,
    handleAddToCart,
    buyableTicketTypes,
    buyableActivityTicketTypes,
    buyableBadgeTypes,
    ...rest,
  };
};

export type UseTicketingReturns = ReturnType<typeof useTicketing>;
